import type { SampleCardDto } from "@hisense/shared";
import { printHtml } from "./printWindow";

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

const printDate = (ms: number | null | undefined) => {
  if (!ms) return "N/A";
  return new Date(ms).toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const absUrl = (url: string | null | undefined): string | null => {
  const v = (url ?? "").trim();
  if (!v) return null;
  if (/^(https?:|data:|blob:)/i.test(v)) return v;
  const origin = window.location.origin;
  return `${origin}${v.startsWith("/") ? "" : "/"}${v}`;
};

const dash = (v: string | null | undefined) => {
  const s = (v ?? "").trim();
  return s ? esc(s) : "-";
};

/**
 * Sample Card print — layout matches generateSampleCardPrintHTML in the
 * legacy SampleCard.js: A4 portrait, bilingual product table, large sample
 * image (or "attach physical sample" placeholder).
 */
export function buildSampleCardHtml(card: SampleCardDto) {
  const logoSrc = `${window.location.origin}/brand/hisense-logo.webp`;
  const img = absUrl(card.imageUrl);
  const generated = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const imageBlock = img
    ? `<img src="${esc(img)}" alt="Product Sample"
         onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
       <div style="display:none;padding:80px 40px;background:#e0e0e0;border:2px dashed #999;color:#666;font-style:italic;text-align:center;">
         Image failed to load - Please attach physical sample here
       </div>`
    : `<div style="padding:80px 40px;background:#e0e0e0;border:2px dashed #999;color:#666;font-style:italic;text-align:center;min-height:240px;display:flex;align-items:center;justify-content:center;width:100%;box-sizing:border-box;">
         <div>
           <div style="font-size:14px;font-weight:bold;margin-bottom:8px;">ATTACH PHYSICAL SAMPLE HERE</div>
           <div style="font-size:11px;">请在此处粘贴实物样品</div>
         </div>
       </div>`;

  const remarksRow = card.remarks?.trim()
    ? `<tr>
        <td style="background:#f0f0f0;font-weight:bold;">Remarks / 备注</td>
        <td colspan="3">${esc(card.remarks)}</td>
      </tr>`
    : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${esc(card.sampleCardNumber || "Sample-Card")}</title>
  <style>
    @page { margin: 8mm; size: A4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    * { box-sizing: border-box; }
    body {
      font-family: Arial, sans-serif; margin: 0; padding: 10px; color: #000;
      line-height: 1.2; font-size: 10px; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    tr { break-inside: avoid; }
    td { overflow-wrap: anywhere; }
    th, td { border: 1px solid #000; padding: 4px 6px; text-align: left; vertical-align: top; }
    .header-section {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 8px;
    }
    .product-image-section {
      text-align: center; margin: 15px 0 10px; padding: 15px;
      border: 2px solid #000; background: #f9f9f9; min-height: 360px; break-inside: avoid;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
    }
    .product-image-section img {
      max-width: 100%; max-height: 320px; border: 2px solid #ddd; object-fit: contain;
    }
    .footer-section {
      text-align: center; margin-top: 10px; padding-top: 6px;
      border-top: 1px solid #000; font-size: 8px;
    }
    @media print {
      body { margin: 0; padding: 8px; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    }
  </style>
</head>
<body>
  <div class="header-section">
    <div>
      <img src="${esc(logoSrc)}" alt="Hisense Button Co., Ltd"
        style="max-height:50px;max-width:200px;object-fit:contain;"
        onerror="this.style.display='none';this.nextElementSibling.style.display='block';" />
      <div style="display:none;padding:8px;border:2px solid #000;max-width:250px;">
        <strong>Hisense Button Co., Ltd</strong>
      </div>
    </div>
    <div style="text-align:right;">
      <h1 style="margin:0;color:#2563eb;font-size:20px;font-weight:bold;">SAMPLE CARD</h1>
      <p style="margin:3px 0 0;font-size:11px;font-family:'Courier New',monospace;">
        <strong>Card #: ${esc(card.sampleCardNumber || "N/A")}</strong>
      </p>
      <p style="margin:2px 0 0;font-size:10px;">Created: <strong>${printDate(card.createdAt)}</strong></p>
    </div>
  </div>

  <table style="border:2px solid #000;">
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Sample Card No.</td>
      <td style="width:20%;">${esc(card.sampleCardNumber || "")}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:10%;">Customer:</td>
      <td style="width:25%;">${dash(card.customerName)}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:10%;">Date:</td>
      <td style="width:20%;">${printDate(card.createdAt)}</td>
    </tr>
  </table>

  <table style="border:2px solid #000;">
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Contact Person:</td>
      <td style="width:35%;">${dash(card.contactPerson)}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Phone:</td>
      <td style="width:35%;">${dash(card.contactPhone)}</td>
    </tr>
    ${/* Email chỉ in khi phiếu có — 69/94 phiếu cũ mang email của đúng người
          liên hệ đã chốt, và nhân viên cần nó để gửi lại mẫu mà không phải đi
          tra danh bạ. 6 phiếu thuộc khách đã bị xoá thì đây là chỗ duy nhất
          còn giữ. */""}
    ${card.contactEmail ? `<tr>
      <td style="background:#f0f0f0;font-weight:bold;">Email:</td>
      <td colspan="3">${esc(card.contactEmail)}</td>
    </tr>` : ""}
  </table>

  <table style="border:2px solid #000;margin-top:10px;">
    <tr>
      <td colspan="4" style="background:#2563eb;color:#fff;font-weight:bold;padding:6px;font-size:11px;">
        Product Details / 产品详情
      </td>
    </tr>
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Product No. / 产品编号</td>
      <td style="width:35%;">${dash(card.productNo)}</td>
      <td style="background:#f0f0f0;font-weight:bold;width:15%;">Type / 类型</td>
      <td style="width:35%;">${dash(card.type)}</td>
    </tr>
    <tr><td style="background:#f0f0f0;font-weight:bold;">Sub Ref / 子参考</td><td colspan="3" style="font-family:monospace;">${dash(card.subRef)}</td></tr>
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;">Brand / 品牌</td>
      <td>${dash(card.brand)}</td>
      <td style="background:#f0f0f0;font-weight:bold;">Color / 颜色</td>
      <td>${dash(card.color)}</td>
    </tr>
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;">Description / 描述</td>
      <td colspan="3">${dash(card.description)}</td>
    </tr>
    <tr>
      <td style="background:#f0f0f0;font-weight:bold;">Size / 尺寸</td>
      <td colspan="3">${dash(card.size)}</td>
    </tr>
    ${remarksRow}
  </table>

  <div class="product-image-section">
    <strong style="display:block;margin-bottom:8px;font-size:11px;color:#2563eb;">Product Sample / 产品样品</strong>
    ${imageBlock}
  </div>

  <div class="footer-section">
    <p style="margin:0;">Document generated on ${esc(generated)}</p>
  </div>
</body>
</html>`;

  return html;
}

export function printSampleCard(card:SampleCardDto) { printHtml(buildSampleCardHtml(card)); }
